import { useState, useEffect } from "react";
import { useLoadScript } from "@react-google-maps/api";
import usePlacesAutoComplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import { Combobox, ComboboxInput, ComboboxPopover, ComboboxList, ComboboxOption } from '@reach/combobox';
import i18n from "../../../i18n";
import './styles.css';
import { setDefaults } from "react-geocode";
import { TypeAddress } from "../../../constants/enums";
import config from "../../../constants/settings";
import { getFrequentAddresses } from "../../../constants/utils";

export default function PlacesServices({
    setSelectedOrigin,
    selectedOrigin,
    setSelectedDestiny,
    selectedDestiny,
    disabled,
    id,
    selectedAddress,
    setSelectedAddress,
    typeAddress,
    setAddressesMarkers,
    addressesMarkers,
    setOnFocusAddress,
    rowsData,
    rowsIndex,
    setRowsScale,
    companie,
    employeeAddresses = []
}) {
    const [libraries] = useState(['places']);
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: config.google.secret,
        libraries: libraries,
        language: "es",
        region: "mx",
    });

    const [valuePlace, setValuePlace] = useState();
    const [locationState, setLocationState] = useState({
        latitude: "",
        longitude: "",
        useLatLng: false,
    });

    const handleCheckboxChange = () => {
        setLocationState((prevState) => ({
            ...prevState,
            useLatLng: !prevState.useLatLng,
        }));
    };

    const frequentAddresses = getFrequentAddresses(companie, employeeAddresses);

    useEffect(() => {
        setDefaults({
            key: import.meta.env.VITE_APP_SECRET_GOOGLE,
            language: "es",
            region: "mx",
        });

        let findMarker;
        switch (typeAddress) {
            case TypeAddress.ARRAY_EMPLOYEES:
                findMarker = addressesMarkers.filter((item) => item.idDB === rowsIndex?.idDB).shift();
                break;
            case TypeAddress.ARRAY_SCALES:
                findMarker = rowsData?.filter((item) => item.id === rowsIndex?.id).shift();
                setValuePlace(findMarker?.description || i18n.t("ScaleAddress"));
                break;
            case TypeAddress.ORIGIN:
                findMarker = addressesMarkers.filter((item) => item.typeAddress === TypeAddress.ORIGIN).shift();
                setValuePlace(findMarker?.description || i18n.t("OriginAddress"));
                break;
            case TypeAddress.DESTINY:
                findMarker = addressesMarkers.filter((item) => item.typeAddress === TypeAddress.DESTINY).shift();
                setValuePlace(findMarker?.description || i18n.t("DestinyAddress"));
                break;
        }
    }, [typeAddress, addressesMarkers, rowsData, rowsIndex]);

    useEffect(() => {
        if (locationState.useLatLng && locationState.latitude && locationState.longitude) {
            const lat = parseFloat(locationState.latitude);
            const lng = parseFloat(locationState.longitude);

            if (!isNaN(lat) && !isNaN(lng)) {
                let onSelectedAddress = { ...selectedAddress };

                switch (typeAddress) {
                    case TypeAddress.ORIGIN:
                        onSelectedAddress.id = 1;
                        break;
                    case TypeAddress.DESTINY:
                        onSelectedAddress.id = 2;
                        break;
                    case TypeAddress.ARRAY_SCALES:
                        if (rowsIndex) {
                            onSelectedAddress.id = rowsIndex.id;
                        } else {
                            onSelectedAddress.id = `temp-${Date.now()}`;
                        }
                        break;
                }

                onSelectedAddress.description = `Lat: ${lat}, Lng: ${lng}`;
                onSelectedAddress.dragg = true;
                onSelectedAddress.typeAddress = typeAddress;
                onSelectedAddress.location = {
                    lat,
                    lon: lng,
                    lng,
                };

                let onAddressesMarkers = [...addressesMarkers];
                if (typeAddress === TypeAddress.ARRAY_EMPLOYEES && rowsIndex) {
                    onSelectedAddress.idDB = rowsIndex.id;
                }

                const idMarker = rowsIndex?.id || onSelectedAddress.id;
                const findAddressesMarkers = onAddressesMarkers.find(item =>
                    typeAddress === TypeAddress.ARRAY_SCALES ? item.id === idMarker : item.typeAddress === typeAddress
                );

                if (findAddressesMarkers) {
                    onAddressesMarkers = onAddressesMarkers.filter(item => item !== findAddressesMarkers);
                }
                onAddressesMarkers.push(onSelectedAddress);
                onAddressesMarkers.sort((a, b) => a.id - b.id);
                setAddressesMarkers(onAddressesMarkers);

                setOnFocusAddress({});
                setSelectedAddress(onSelectedAddress);

                switch (typeAddress) {
                    case TypeAddress.ORIGIN:
                        setSelectedOrigin({ lat, lng });
                        break;
                    case TypeAddress.DESTINY:
                        setSelectedDestiny({ lat, lng });
                        break;
                    case TypeAddress.ARRAY_SCALES:
                        updateRowsData(onSelectedAddress);
                        break;
                }

                setValuePlace(`Lat: ${lat}, Lng: ${lng}`);
            }
        }
    }, [locationState]);

    useEffect(() => {
        const autoSelectOriginAddress = async () => {
            if (
                [3867, 4355, 3996, 4362, 7784].includes(companie) &&
                typeAddress === TypeAddress.ORIGIN &&
                addressesMarkers.filter((item) => item.typeAddress === TypeAddress.ORIGIN).length === 0
            ) {
                const frequentAddress = getFrequentAddresses(companie)[0];
                if (frequentAddress) {
                    await handleSelect(frequentAddress);
                }
            }
        };
    
        autoSelectOriginAddress();
    }, [companie, typeAddress, addressesMarkers]);    

    const {
        ready,
        value,
        setValue,
        suggestions: { status, data },
        clearSuggestions
    } = usePlacesAutoComplete({
        requestOptions: {
            componentRestrictions: { country: 'mx' },
        },
        debounce: 300,
    });

    const handleSelect = async (address) => {
        const results = await getGeocode({ address });
        const { lat, lng } = await getLatLng(results[0]);
        setValue(address, false);
        clearSuggestions();
        let onSelectedAddress = { ...selectedAddress };

        switch (typeAddress) {
            case TypeAddress.ORIGIN:
                onSelectedAddress.id = 1;
                break;
            case TypeAddress.DESTINY:
                onSelectedAddress.id = 2;
                break;
            case TypeAddress.ARRAY_SCALES:
                if (rowsIndex) {
                    onSelectedAddress.id = rowsIndex.id;
                } else {
                    onSelectedAddress.id = `temp-${Date.now()}`;
                }
                break;
        }

        onSelectedAddress.description = address;
        onSelectedAddress.dragg = true;
        onSelectedAddress.typeAddress = typeAddress;
        onSelectedAddress.location = {
            lat,
            lon: lng,
            lng,
        };

        let onAddressesMarkers = [...addressesMarkers];
        if (typeAddress === TypeAddress.ARRAY_EMPLOYEES && rowsIndex) {
            onSelectedAddress.idDB = rowsIndex.id;
        }

        const idMarker = rowsIndex?.id || onSelectedAddress.id;
        const findAddressesMarkers = onAddressesMarkers.find(item =>
            typeAddress === TypeAddress.ARRAY_SCALES ? item.id === idMarker : item.typeAddress === typeAddress
        );

        if (findAddressesMarkers) {
            onAddressesMarkers = onAddressesMarkers.filter(item => item !== findAddressesMarkers);
        }
        onAddressesMarkers.push(onSelectedAddress);
        onAddressesMarkers.sort((a, b) => a.id - b.id);
        setAddressesMarkers(onAddressesMarkers);

        setOnFocusAddress({});
        setSelectedAddress(onSelectedAddress);

        switch (typeAddress) {
            case TypeAddress.ORIGIN:
                setSelectedOrigin({ lat, lng });
                break;
            case TypeAddress.DESTINY:
                setSelectedDestiny({ lat, lng });
                break;
            case TypeAddress.ARRAY_SCALES:
                updateRowsData(onSelectedAddress);
                break;
        }
        setValuePlace(address);
    };

    const updateRowsData = (onSelectedAddress) => {
        if (!rowsIndex) return;
        
        let onRowsData = rowsData || [];
        const findRow = onRowsData.filter((item) => item.id === rowsIndex.id).shift();
        if (findRow) {
            onRowsData.splice(onRowsData.indexOf(findRow), 1);
            findRow.description = onSelectedAddress.description;
            findRow.dragg = true;
            findRow.location = onSelectedAddress.location;
            onRowsData.push(findRow);
            onRowsData.sort((a, b) => (a.id > b.id) ? 1 : -1);
            setRowsScale([...onRowsData]);
        }
    };

    useEffect(() => {
        if (selectedAddress && selectedAddress.description) {
            setValue(selectedAddress.description);
        }
    }, [selectedAddress]);   

    if (!isLoaded) return <div>Loading...</div>;

    return (
        <div>
        <Combobox onSelect={handleSelect}>
            <ComboboxInput
                value={value}
                placeholder={valuePlace}
                id={id}
                key={id}
                onFocus={(e) => setOnFocusAddress(e)}
                onClick={(e) => setOnFocusAddress(e)}
                onChange={(e) => setValue(e.target.value)}
                disabled={disabled}
                className="combobox-input w-100 form-control"
            />
            {status === 'OK' && value && !locationState.useLatLng && (
                <ComboboxPopover className="places">
                    <ComboboxList>
                        {companie && frequentAddresses.length > 0 && value && value.length < 5 && (
                            <div className="frequent-addresses-section">
                                <div className="frequent-addresses-title">
                                    {i18n.t("FrequentAddresses")}
                                </div>
                                {getFrequentAddresses(companie).length > 0 && (
                                    <>
                                        {getFrequentAddresses(companie).map((address, key) => (
                                            <div className="list-places" key={`company-${key}`}>
                                                <ComboboxOption 
                                                    className="place company-address" 
                                                    value={address} 
                                                />
                                            </div>
                                        ))}
                                    </>
                                )}
                                {employeeAddresses.length > 0 && (
                                    <>
                                        {employeeAddresses.map((addr, key) => (
                                            <div className="list-places" key={`employee-${key}`}>
                                                <ComboboxOption 
                                                    className="place employee-address" 
                                                    value={addr.address.description} 
                                                />
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>
                        )}
                        {data.map((item, key) => (
                            <div className="list-places" key={`search-${key}`}>
                                <ComboboxOption 
                                    className="place" 
                                    value={item.description} 
                                />
                            </div>
                        ))}
                    </ComboboxList>
                </ComboboxPopover>
            )}
        </Combobox>
            <div className="lat-lng-container">
                <label className="checkbox-label">
                    <input
                        type="checkbox"
                        onChange={handleCheckboxChange}
                        className="lat-lng-checkbox"
                    />
                    {i18n.t("UseLatLng")}
                </label>
            </div>
            {locationState.useLatLng && (
                <div className="lat-lng-inputs">
                    <div className="input-group">
                        <label className="input-title">{i18n.t("Latitude")}</label>
                        <input
                            type="text"
                            placeholder={i18n.t("WriteLatitude")}
                            value={locationState.latitude}
                            onChange={(e) => setLocationState({ ...locationState, latitude: e.target.value })}
                            className="lat-lng-input"
                        />
                    </div>
                    <div className="input-group">
                        <label className="input-title">{i18n.t("Longitude")}</label>
                        <input
                            type="text"
                            placeholder={i18n.t("WriteLongitude")}
                            value={locationState.longitude}
                            onChange={(e) => setLocationState({ ...locationState, longitude: e.target.value })}
                            className="lat-lng-input"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
